import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Check, X } from "lucide-react";

interface MultiDateCalendarProps {
  selectedDates: Date[];
  minDate: Date;
  onToggleDate: (date: Date) => void;
  onClearAll: () => void;
  onSelectAllMonth: (month: Date) => void;
}

const MultiDateCalendar = ({
  selectedDates,
  minDate,
  onToggleDate,
  onClearAll,
  onSelectAllMonth
}: MultiDateCalendarProps) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  
  const [displayDate, setDisplayDate] = useState(() => {
    // Start from the month of minDate or current month
    return new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  });

  const translations = {
    datesSelected: isRTL ? 'تاريخ محدد' : 'dates selected',
    clearAll: isRTL ? 'مسح الكل' : 'Clear All',
    selectAll: isRTL ? 'تحديد كل الشهر' : 'Select All',
    selectSpecificDates: isRTL ? 'حدد التواريخ المحددة لحملتك' : 'Select specific dates for your campaign'
  };

  const dayNames = isRTL 
    ? ['أحد', 'إثن', 'ثلث', 'أربع', 'خمس', 'جمعة', 'سبت']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const monthNames = isRTL
    ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
    : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const navigateMonth = (direction: 'prev' | 'next') => {
    setDisplayDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  const isDateSelected = (date: Date): boolean => {
    return selectedDates.some(d => 
      d.getFullYear() === date.getFullYear() &&
      d.getMonth() === date.getMonth() &&
      d.getDate() === date.getDate()
    );
  };

  const isDateDisabled = (date: Date): boolean => {
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const minDateOnly = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
    return dateOnly < minDateOnly;
  };

  const canNavigatePrev = () => {
    const prevMonth = new Date(displayDate);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    const lastDayOfPrevMonth = new Date(prevMonth.getFullYear(), prevMonth.getMonth() + 1, 0);
    return lastDayOfPrevMonth >= minDate;
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = displayDate.getFullYear();
    const month = displayDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days: (number | null)[] = [];
    
    // Add empty slots for days before the first day
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    
    // Add actual days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const days = generateCalendarDays();

  return (
    <div className="bg-card dark:bg-card p-3 sm:p-4 rounded-lg border border-border">
      {/* Header */}
      <p className="text-sm text-muted-foreground mb-4 text-center">
        {translations.selectSpecificDates}
      </p>
      
      {/* Month Navigation */}
      <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigateMonth('prev')}
          disabled={!canNavigatePrev()}
          className="h-8 w-8"
        >
          {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
        
        <h3 className="font-semibold text-foreground">
          {monthNames[displayDate.getMonth()]} {displayDate.getFullYear()}
        </h3>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigateMonth('next')}
          className="h-8 w-8"
        >
          {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </Button>
      </div>

      {/* Day Headers */}
      <div className={`grid grid-cols-7 gap-1 mb-2 ${isRTL ? 'direction-rtl' : ''}`}>
        {dayNames.map((day, index) => (
          <div key={index} className="text-center text-xs font-medium text-muted-foreground py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, index) => {
          if (day === null) {
            return <div key={`empty-${index}`} className="h-11 sm:h-10" />;
          }
          
          const date = new Date(displayDate.getFullYear(), displayDate.getMonth(), day, 12, 0, 0);
          const isSelected = isDateSelected(date);
          const isDisabled = isDateDisabled(date);
          
          return (
            <button
              key={day}
              type="button"
              onClick={() => !isDisabled && onToggleDate(date)}
              disabled={isDisabled}
              className={`
                h-11 sm:h-10 w-full rounded-md text-sm font-medium transition-all relative touch-manipulation active:scale-95
                ${isDisabled 
                  ? 'text-muted-foreground/40 cursor-not-allowed' 
                  : isSelected 
                    ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90' 
                    : 'hover:bg-muted text-foreground'}
              `}
            >
              {day}
              {isSelected && (
                <Check className="absolute top-0.5 right-0.5 h-3 w-3" />
              )}
            </button>
          );
        })}
      </div>

      {/* Footer with count and actions */}
      <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-4 pt-4 border-t border-border ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-primary">{selectedDates.length}</span>
          <span className="text-sm text-muted-foreground">{translations.datesSelected}</span>
        </div>
        
        <div className="flex gap-2 w-full sm:w-auto [&>button]:flex-1 sm:[&>button]:flex-none">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClearAll}
            disabled={selectedDates.length === 0}
            className="text-xs"
          >
            <X className="h-3 w-3 mr-1" />
            {translations.clearAll}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onSelectAllMonth(displayDate)}
            className="text-xs"
          >
            {translations.selectAll}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MultiDateCalendar;
